import { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { Search, CheckCircle, Plane, X, ArrowRight, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { FLIGHT_AWARE_API_BASE } from '../config/environment.js';
import { authFetch } from '../utils/authFetch';
import airportTimezoneService from '../services/airportTimezoneService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTodayLocal = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

const FlightTime = ({ timeString, airportCode }) => {
  const [formatted, setFormatted] = useState('—');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!timeString) {
        if (!cancelled) setFormatted('—');
        return;
      }
      try {
        const code = (airportCode || '').toUpperCase().replace(/^K/, '');
        const tz = code ? await airportTimezoneService.getAirportTimezone(code) : 'UTC';
        const d = new Date(timeString);
        const out = d.toLocaleString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
          timeZone: tz
        });
        if (!cancelled) setFormatted(out);
      } catch {
        if (!cancelled) setFormatted(new Date(timeString).toLocaleTimeString());
      }
    })();
    return () => { cancelled = true; };
  }, [timeString, airportCode]);

  return formatted;
};

// ─── Search helpers ───────────────────────────────────────────────────────────

const buildSearchParams = ({ departureAirport, arrivalAirport, date, airline, windowHoursOverride }) => {
  const params = new URLSearchParams({
    origin: departureAirport,
    destination: arrivalAirport,
    date,
  });
  if (airline) params.append('airline', airline);

  // If searching for today, include current time so the backend returns
  // upcoming flights first. For future dates, omit startTime.
  const now = new Date();
  const todayLocal = formatTodayLocal(now);
  const searchingToday = date === todayLocal;
  if (searchingToday) {
    params.append('startTime', now.toISOString().substring(0, 19) + 'Z');
  }
  if (windowHoursOverride && searchingToday) {
    params.append('windowHours', String(windowHoursOverride));
  }
  return params;
};

// Filter flights to only upcoming departures within the optional time window
const filterUpcomingFlights = (flights, windowHours) => {
  const nowMs = Date.now();
  const windowCutoffMs = windowHours
    ? nowMs + (windowHours * 60 * 60 * 1000)
    : Infinity;
  return flights.filter(f => {
    const dep = new Date(f.scheduledOut || 0).getTime();
    return dep > nowMs && dep <= windowCutoffMs;
  });
};

const processSearchResults = (data, windowHoursOverride) => {
  if (!data.flights || data.flights.length === 0) {
    return { flights: [], toastMsg: 'No flights found for that route and date', toastType: 'error' };
  }

  const upcoming = filterUpcomingFlights(data.flights, windowHoursOverride);

  if (upcoming.length === 0) {
    const msg = data.flights.length > 0
      ? 'All matching flights have already departed'
      : 'No flights found for that route and date';
    return { flights: [], toastMsg: msg, toastType: 'error' };
  }

  return {
    flights: upcoming,
    fullData: data,
    toastMsg: `Found ${upcoming.length} upcoming flight${upcoming.length > 1 ? 's' : ''}`,
    toastType: 'success',
  };
};

// ─── Flight-select helpers ────────────────────────────────────────────────────

const buildDateRange = (flight, formDate) => {
  const flightDate = (flight.scheduledOut || '').substring(0, 10) || formDate;
  const startObj = new Date(`${flightDate}T00:00:00Z`);
  const endObj = new Date(`${flightDate}T00:00:00Z`);
  endObj.setUTCDate(endObj.getUTCDate() + 1);
  return {
    startDate: startObj.toISOString().substring(0, 10),
    endDate: endObj.toISOString().substring(0, 10),
  };
};

const normalizeAirportCode = (code) => (code || '').toUpperCase().replace(/^K/, '');

const findMatchingLeg = (flights, wantOrigin, wantDest, wantScheduled) => {
  const list = flights || [];
  return list.find(f => f.scheduled_out === wantScheduled)
    || list.find(f => {
      const orig = normalizeAirportCode(f.origin?.code_iata || f.origin?.code);
      const dest = normalizeAirportCode(f.destination?.code_iata || f.destination?.code);
      return orig === wantOrigin && dest === wantDest;
    })
    || list[0]
    || null;
};

/**
 * Fetch the detailed leg data for a selected flight from FlightAware.
 * Throws on network / API errors or when no matching leg is found.
 */
const fetchFlightLeg = async (flight, formDate, departureAirport, arrivalAirport) => {
  const { startDate, endDate } = buildDateRange(flight, formDate);

  const response = await authFetch(
    `${FLIGHT_AWARE_API_BASE}/${encodeURIComponent(flight.ident)}?start=${startDate}&end=${endDate}`
  );
  if (!response.ok) throw new Error('Failed to load flight details');

  const data = await response.json();

  const wantOrigin = normalizeAirportCode(departureAirport);
  const wantDest = normalizeAirportCode(arrivalAirport);
  const leg = findMatchingLeg(data.flights, wantOrigin, wantDest, flight.scheduledOut);

  if (!leg) throw new Error('Could not load flight details');
  return leg;
};

/**
 * Parse an error response body, returning a descriptive Error.
 */
const parseResponseError = async (response) => {
  const body = await response.json().catch(() => ({}));
  return new Error(body.error || `Search failed: ${response.status}`);
};

// Helper: pick the first truthy value from a list of candidates
const firstOf = (...values) => {
  for (const v of values) {
    if (v != null && v !== '') return v;
  }
  return null;
};

const buildTripFlight = (flight, leg) => {
  // Prefer estimated times over scheduled (indicates delays).
  // Fall back through multiple field names for robustness.
  const depTime = firstOf(leg.estimated_out, leg.scheduled_out, leg.scheduled_off, flight.scheduledOut);
  const arrTime = firstOf(leg.estimated_in, leg.scheduled_in, leg.scheduled_on, flight.scheduledIn);

  return {
    flightId: flight.ident,
    airline: flight.airline,
    flightNumber: String(flight.flightNumber),
    departureAirport: flight.origin.code,
    arrivalAirport: flight.destination.code,
    departureTime: depTime,
    arrivalTime: arrTime,
    scheduledDepartureTime: leg.scheduled_out || flight.scheduledOut,
    scheduledArrivalTime: leg.scheduled_in || flight.scheduledIn,
    departureGate: leg.gate_origin || null,
    departureTerminal: leg.terminal_origin || null,
    arrivalGate: leg.gate_destination || null,
    arrivalTerminal: leg.terminal_destination || null,
    baggageClaim: leg.baggage_claim || null,
    status: leg.status || 'Scheduled',
    aircraft: firstOf(leg.aircraft_type, flight.aircraftType),
  };
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px',
};
const inputStyle = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '16px',
  textTransform: 'uppercase',
  boxSizing: 'border-box',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SearchFormHeader = ({ onCancel }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
    <h3 style={{
      fontSize: '18px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }}>
      <Search size={20} style={{ color: '#0ea5e9' }} />
      Find Your Flight
    </h3>
    {onCancel && (
      <button
        onClick={onCancel}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}
      >
        <X size={18} />
      </button>
    )}
  </div>
);

const SearchFormFields = ({ form, loading, updateField }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
    <div>
      <label style={labelStyle}>From *</label>
      <input
        type="text"
        value={form.departureAirport}
        onChange={(e) => updateField('departureAirport', e.target.value)}
        placeholder="DFW"
        maxLength={4}
        disabled={loading}
        style={inputStyle}
      />
    </div>
    <div>
      <label style={labelStyle}>To *</label>
      <input
        type="text"
        value={form.arrivalAirport}
        onChange={(e) => updateField('arrivalAirport', e.target.value)}
        placeholder="LAX"
        maxLength={4}
        disabled={loading}
        style={inputStyle}
      />
    </div>
    <div>
      <label style={labelStyle}>Date *</label>
      <input
        type="date"
        value={form.date}
        onChange={(e) => updateField('date', e.target.value)}
        disabled={loading}
        style={{ ...inputStyle, textTransform: 'none' }}
      />
    </div>
    <div>
      <label style={labelStyle}>Airline (optional)</label>
      <input
        type="text"
        value={form.airline}
        onChange={(e) => updateField('airline', e.target.value)}
        placeholder="AA"
        maxLength={3}
        disabled={loading}
        style={inputStyle}
      />
    </div>
  </div>
);

const ErrorBanner = ({ error }) => {
  if (!error) return null;
  return (
    <div style={{
      padding: '12px',
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '16px',
    }}>
      {error}
    </div>
  );
};

const QuickSearchButton = ({ loading, disabled, onQuickSearch }) => (
  <button
    onClick={() => onQuickSearch(3)}
    disabled={disabled}
    title="Search flights departing within the next 3 hours"
    style={{
      width: '100%',
      padding: '10px 16px',
      backgroundColor: disabled ? '#f3f4f6' : '#fef3c7',
      color: disabled ? '#9ca3af' : '#92400e',
      border: '1px solid ' + (disabled ? '#e5e7eb' : '#fcd34d'),
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      marginBottom: '10px',
    }}
  >
    <Clock size={16} />
    Flights in the next 3 hours
  </button>
);

const SearchButton = ({ loading, onSearch }) => (
  <button
    onClick={() => onSearch()}
    disabled={loading}
    style={{
      width: '100%',
      padding: '12px 20px',
      backgroundColor: loading ? '#9ca3af' : '#0ea5e9',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: loading ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    }}
  >
    <Search size={18} />
    {loading ? 'Searching…' : 'Search Flights'}
  </button>
);

const FlightResultCard = ({ flight, isSelected, addingFlight, onSelect }) => (
  <button
    key={flight.ident}
    onClick={() => onSelect(flight)}
    disabled={addingFlight}
    style={{
      width: '100%',
      textAlign: 'left',
      padding: '14px 16px',
      backgroundColor: isSelected ? '#dbeafe' : 'white',
      border: isSelected ? '2px solid #0ea5e9' : '1px solid #e5e7eb',
      borderRadius: '8px',
      cursor: addingFlight ? 'not-allowed' : 'pointer',
      opacity: addingFlight && !isSelected ? 0.5 : 1,
      transition: 'all 150ms ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
    }}
  >
    <div style={{ flex: 1 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '6px',
      }}>
        <Plane size={16} style={{ color: '#0ea5e9' }} />
        <span style={{ fontWeight: '700', fontSize: '16px', color: '#1f2937' }}>
          {flight.airline} {flight.flightNumber}
        </span>
        {flight.aircraftType && (
          <span style={{ fontSize: '12px', color: '#6b7280' }}>
            {flight.aircraftType}
          </span>
        )}
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        color: '#4b5563',
      }}>
        <span><strong>{flight.origin.code}</strong> <FlightTime timeString={flight.scheduledOut} airportCode={flight.origin.code} /></span>
        <ArrowRight size={14} style={{ color: '#9ca3af' }} />
        <span><strong>{flight.destination.code}</strong> <FlightTime timeString={flight.scheduledIn} airportCode={flight.destination.code} /></span>
      </div>
    </div>
    {isSelected && addingFlight ? (
      <span style={{ fontSize: '12px', color: '#0ea5e9' }}>Adding…</span>
    ) : (
      <CheckCircle size={20} style={{ color: isSelected ? '#0ea5e9' : '#d1d5db' }} />
    )}
  </button>
);

const FlightResultsList = ({ results, form, selectedIdent, addingFlight, onSelect }) => {
  if (!results) return null;

  if (results.flights.length === 0) {
    return (
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#fff7ed',
        border: '1px solid #fed7aa',
        borderRadius: '8px',
        color: '#9a3412',
        fontSize: '14px',
      }}>
        No flights found. Try removing the airline filter, or check the airports and date.
      </div>
    );
  }

  return (
    <div style={{ marginTop: '20px' }}>
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        color: '#6b7280',
        marginBottom: '10px',
      }}>
        {results.flights.length} flight{results.flights.length > 1 ? 's' : ''} on {form.date}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {results.flights.map((f) => (
          <FlightResultCard
            key={f.ident}
            flight={f}
            isSelected={selectedIdent === f.ident}
            addingFlight={addingFlight}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * FlightSearch
 *
 * Lets the user search flights between two airports on a given date (and
 * optionally filter by airline), then pick a specific flight from the
 * results. The selected flight is passed to `onFlightFound` with full
 * scheduled details.
 */
function FlightSearch({ onFlightFound, onCancel, defaultDate, defaultDepartureAirport, defaultArrivalAirport }) {
  const [form, setForm] = useState({
    airline: '',
    departureAirport: defaultDepartureAirport || '',
    arrivalAirport: defaultArrivalAirport || '',
    date: defaultDate || formatTodayLocal(),
  });

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [selectedIdent, setSelectedIdent] = useState(null);
  const [addingFlight, setAddingFlight] = useState(false);
  const [quickFilterHours, setQuickFilterHours] = useState(null); // null = no filter; 3 = next 3 hours

  useEffect(() => {
    if (defaultDate) setForm(p => ({ ...p, date: defaultDate }));
    if (defaultDepartureAirport) setForm(p => ({ ...p, departureAirport: defaultDepartureAirport }));
    if (defaultArrivalAirport) setForm(p => ({ ...p, arrivalAirport: defaultArrivalAirport }));
  }, [defaultDate, defaultDepartureAirport, defaultArrivalAirport]);

  const updateField = (field, value) => {
    const normalized = field === 'date' ? value : value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setForm(p => ({ ...p, [field]: normalized }));
    if (error) setError(null);
    // User is editing the form manually — clear any quick-filter cap
    if (quickFilterHours !== null) setQuickFilterHours(null);
  };

  const handleSearch = async (overrides = {}) => {
    const { airline, departureAirport, arrivalAirport, date } = { ...form, ...overrides };
    const windowHoursOverride = overrides.windowHours ?? quickFilterHours;

    if (!departureAirport || !arrivalAirport || !date) {
      setError('Departure airport, arrival airport, and date are required');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setSelectedIdent(null);

    try {
      const params = buildSearchParams({ departureAirport, arrivalAirport, date, airline, windowHoursOverride });

      toast.loading('Searching flights...', { id: 'search' });

      const response = await authFetch(`${FLIGHT_AWARE_API_BASE}/search?${params.toString()}`);
      if (!response.ok) throw await parseResponseError(response);

      const data = await response.json();
      const result = processSearchResults(data, windowHoursOverride);

      if (result.toastType === 'error') {
        setResults({ flights: [] });
        toast.error(result.toastMsg, { id: 'search' });
        return;
      }

      setResults({ ...result.fullData, flights: result.flights });
      toast.success(result.toastMsg, { id: 'search' });
    } catch (err) {
      setError(err.message);
      toast.error(err.message, { id: 'search' });
    } finally {
      setLoading(false);
    }
  };

  // Quick-search: next N hours. Sets today's date, clears airline filter,
  // and narrows the FA window to N hours for a cheaper 1-page search.
  const handleQuickSearch = async (hours) => {
    const todayLocal = formatTodayLocal();
    setForm(p => ({ ...p, date: todayLocal }));
    setQuickFilterHours(hours);
    await handleSearch({ date: todayLocal, windowHours: hours });
  };

  const handleSelectFlight = async (flight) => {
    if (!onFlightFound) return;

    setSelectedIdent(flight.ident);
    setAddingFlight(true);

    try {
      toast.loading('Getting full flight details...', { id: 'add' });

      const leg = await fetchFlightLeg(flight, form.date, form.departureAirport, form.arrivalAirport);

      logger.debug('[FlightSearch] Selected leg:', {
        ident: leg.ident,
        status: leg.status,
        gate_origin: leg.gate_origin,
        terminal_origin: leg.terminal_origin,
        gate_destination: leg.gate_destination,
        terminal_destination: leg.terminal_destination,
      });

      const tripFlight = buildTripFlight(flight, leg);

      onFlightFound(tripFlight);
      toast.success(`Added ${flight.airline} ${flight.flightNumber}`, { id: 'add' });

      // Reset for next search — pre-fill origin with the arrival airport for connecting flights
      setForm(p => ({
        ...p,
        airline: '',
        departureAirport: flight.destination.code,
        arrivalAirport: '',
      }));
      setResults(null);
      setSelectedIdent(null);
    } catch (err) {
      toast.error(err.message, { id: 'add' });
    } finally {
      setAddingFlight(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const quickSearchDisabled = loading || !form.departureAirport || !form.arrivalAirport;

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '20px',
      backgroundColor: '#f9fafb',
      marginBottom: '20px',
    }}>
      <SearchFormHeader onCancel={onCancel} />
      <SearchFormFields form={form} loading={loading} updateField={updateField} />
      <ErrorBanner error={error} />
      <QuickSearchButton loading={loading} disabled={quickSearchDisabled} onQuickSearch={handleQuickSearch} />
      <SearchButton loading={loading} onSearch={handleSearch} />
      <FlightResultsList
        results={results}
        form={form}
        selectedIdent={selectedIdent}
        addingFlight={addingFlight}
        onSelect={handleSelectFlight}
      />
    </div>
  );
}

export default FlightSearch;
